import type { GetContentModuleResponse, GetContentModulesResponse } from '@/services/api/api.types'

/**
 * A new module is created at the channel's current highest priority rather than above it
 * (backend_common_functions ef0190f2, C2-2406), so equal priorities are normal and every module
 * added since the last reorder ties with the top of the list. The app's Content Library breaks
 * those ties by publication date — sidekick-app contentLibraryUtils.ts — so the portal has to
 * break them the same way, or it ranks tied modules in an order members never see (SME-1131).
 */
const compareByPublishedDateDesc = (a: GetContentModuleResponse, b: GetContentModuleResponse) => {
  const aDate = a.lastPublishedAt ?? a.createdAt
  const bDate = b.lastPublishedAt ?? b.createdAt
  if (aDate && bDate) return new Date(bDate).getTime() - new Date(aDate).getTime()
  if (aDate) return 1
  if (bDate) return -1
  return 0
}

/** Highest priority first, matching the app's Content Library ordering. */
export const sortModulesByPriority = (modules: GetContentModulesResponse): GetContentModulesResponse =>
  [...modules].sort((a, b) => {
    const priorityDiff = (b.priority ?? 0) - (a.priority ?? 0)
    if (priorityDiff !== 0) return priorityDiff

    return compareByPublishedDateDesc(a, b)
  })

/**
 * The priority to persist for a module at `index` of a reordered list of `total`.
 * One distinct value per position: the previous 0-100 scale had only 101 values to spend, so a
 * channel with more modules than that came out of a single drag with tied priorities,
 * reintroducing the ambiguity the drag was meant to resolve.
 */
export const priorityForIndex = (index: number, total: number) => total - index

/**
 * Write a reorder's new priorities onto the cached module list. Patches in place rather than
 * replacing, because the prioritization screen filters help modules out of its own list while the
 * Content Library reads the same cache and counts them — replacing would evict them from both.
 * Returns undefined for an empty cache, which `setQueryData` treats as no update.
 */
export const applyReorderedPriorities = (
  cached: GetContentModulesResponse | undefined,
  reordered: GetContentModulesResponse,
) => {
  const priorityByTaskId = new Map(
    reordered.map((module, index, all) => [module.taskId, priorityForIndex(index, all.length)]),
  )

  return cached?.map(module => {
    const priority = priorityByTaskId.get(module.taskId)
    return priority === undefined ? module : { ...module, priority }
  })
}
